import type { ReactNode } from 'react';
import clsx from 'clsx';
import { InfoIcon, SettingsIcon } from './icons';

interface StandardPageProps {
  title: string;
  infoTooltip?: string;
  onSettingsClick?: () => void;
  toolbar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Page shell enforcing the OOM-1259 layout:
 *  1. Page header (title + info icon, settings on right)
 *  2. Toolbar
 *  3. Body (table / grid)
 *  4. Footer (totals + pagination)
 *
 * Children should typically be a `DataTable` or `CardGrid` wrapped in the
 * same `.bui-scope` so tokens resolve. This shell already opens the scope.
 */
export function StandardPage({
  title,
  infoTooltip,
  onSettingsClick,
  toolbar,
  footer,
  children,
  className,
}: StandardPageProps): JSX.Element {
  return (
    <section
      className={clsx('bui-scope flex h-full min-h-0 flex-col', className)}
      style={{ backgroundColor: 'var(--bui-surface-alt)' }}
    >
      <header
        className="flex items-center justify-between px-5"
        style={{ minHeight: 40, backgroundColor: 'var(--bui-surface)' }}
      >
        <div className="inline-flex items-center gap-2">
          <h1
            className="text-base font-medium"
            style={{ color: 'var(--bui-text-primary)' }}
          >
            {title}
          </h1>
          {infoTooltip && (
            <span
              role="img"
              aria-label={infoTooltip}
              title={infoTooltip}
              style={{ color: 'var(--bui-text-placeholder)' }}
            >
              <InfoIcon />
            </span>
          )}
        </div>
        {onSettingsClick && (
          <button
            type="button"
            aria-label="Page settings"
            title="Page settings"
            onClick={onSettingsClick}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[var(--bui-surface-hover)] focus-visible:outline-none focus-visible:ring-2"
            style={{ color: 'var(--bui-text-secondary)' }}
          >
            <SettingsIcon />
          </button>
        )}
      </header>

      {toolbar}

      <div className="flex-1 min-h-0 overflow-auto px-6 pb-2">{children}</div>

      {footer}
    </section>
  );
}
